import { lazy, Suspense, type ComponentType, type JSX } from 'react';

type JSX_Attrs = JSX.IntrinsicAttributes;
import { ClinicaFallback } from './components/clinica/ClinicaFallback.js';
import { registerClinicPage } from './clinicPreload.js';
import { Route } from 'wouter';

/**
 * Rotas do módulo da clínica (fase 7: a clínica e o lado do paciente, Saúde). Só existem
 * nos pacotes de teste, do dono e na web: o pacote da loja é montado com `VITE_CLINIC=off`
 * (gate G7.5: nada do módulo vai à loja antes do CNPJ, das contas de organização e da
 * declaração de saúde). A condição usa a variável de ambiente crua, como a edição "full"
 * (`AppRoutes.tsx`): o Vite a troca por um literal e descarta os imports dinâmicos, então
 * nenhuma página, texto ou caminho da clínica vai para o pacote da loja.
 */
const page = <P extends object>(load: () => Promise<ComponentType<P>>) => {
  // Depois que o código chegou (pela pré-carga da mesa ou pela primeira visita), a página
  // monta na hora, sem passar pelo Suspense: trocar de página na barra não pisca.
  let ready: ComponentType<P> | null = null;
  const get = () =>
    load().then((component) => {
      ready = component;
      return component;
    });
  registerClinicPage(get);
  const Lazy = lazy(() => get().then((component) => ({ default: component })));
  return (props: P) => {
    const Ready = ready;
    if (Ready) return <Ready {...(props as P & JSX_Attrs)} />;
    return (
      <Suspense fallback={<ClinicaFallback />}>
        <Lazy {...(props as P & JSX_Attrs)} />
      </Suspense>
    );
  };
};

const PAGES =
  import.meta.env.VITE_CLINIC === 'off'
    ? null
    : {
        Hoje: page(() => import('./pages/clinica/HojePage.js').then((m) => m.HojePage)),
        Nova: page(() =>
          import('./pages/clinica/NovaClinicaPage.js').then((m) => m.NovaClinicaPage),
        ),
        Agenda: page(() =>
          import('./pages/clinica/AgendaClinicaPage.js').then((m) => m.AgendaClinicaPage),
        ),
        Pacientes: page(() =>
          import('./pages/clinica/PacientesClinicaPage.js').then((m) => m.PacientesClinicaPage),
        ),
        Video: page(() => import('./pages/clinica/VideoPage.js').then((m) => m.VideoPage)),
        Corpo: page(() => import('./pages/clinica/CorpoPage.js').then((m) => m.CorpoPage)),
        CorpoAluno: page(() =>
          import('./pages/saude/CorpoAlunoPage.js').then((m) => m.CorpoAlunoPage),
        ),
        VideoAluno: page(() =>
          import('./pages/saude/VideoAlunoPage.js').then((m) => m.VideoAlunoPage),
        ),
        Paciente: page(() =>
          import('./pages/clinica/PacienteClinicaPage.js').then((m) => m.PacienteClinicaPage),
        ),
        Atendimento: page(() =>
          import('./pages/clinica/AtendimentoPage.js').then((m) => m.AtendimentoPage),
        ),
        Relatorios: page(() =>
          import('./pages/clinica/RelatoriosPage.js').then((m) => m.RelatoriosPage),
        ),
        Medicamentos: page(() =>
          import('./pages/clinica/MedicamentosPage.js').then((m) => m.MedicamentosPage),
        ),
        Plano: page(() => import('./pages/clinica/PlanoPage.js').then((m) => m.PlanoPage)),
        FichaTreino: page(() =>
          import('./pages/clinica/FichaTreinoPage.js').then((m) => m.FichaTreinoPage),
        ),
        Treinos: page(() => import('./pages/clinica/TreinosPage.js').then((m) => m.TreinosPage)),
        Diarios: page(() => import('./pages/clinica/DiariosPage.js').then((m) => m.DiariosPage)),
        Formulario: page(() =>
          import('./pages/clinica/FormularioPublicoPage.js').then((m) => m.FormularioPublicoPage),
        ),
        Alimentos: page(() =>
          import('./pages/clinica/AlimentosPage.js').then((m) => m.AlimentosPage),
        ),
        Financeiro: page(() =>
          import('./pages/clinica/FinanceiroPage.js').then((m) => m.FinanceiroPage),
        ),
        Tarefas: page(() => import('./pages/clinica/TarefasPage.js').then((m) => m.TarefasPage)),
        Leads: page(() => import('./pages/clinica/LeadsPage.js').then((m) => m.LeadsPage)),
        Numeros: page(() =>
          import('./pages/clinica/NumerosClinicaPage.js').then((m) => m.NumerosClinicaPage),
        ),
        Mais: page(() => import('./pages/clinica/MaisPage.js').then((m) => m.MaisPage)),
        PaginaPublica: page(() =>
          import('./pages/clinica/PaginaPublicaPage.js').then((m) => m.PaginaPublicaPage),
        ),
        Catalogo: page(() => import('./pages/clinica/CatalogoPage.js').then((m) => m.CatalogoPage)),
        Equipe: page(() => import('./pages/clinica/EquipePage.js').then((m) => m.EquipePage)),
        Importar: page(() => import('./pages/clinica/ImportarPage.js').then((m) => m.ImportarPage)),
        Importacao: page(() =>
          import('./pages/clinica/ImportacaoPage.js').then((m) => m.ImportacaoPage),
        ),
        Entrar: page(() =>
          import('./pages/clinica/EntrarClinicaPage.js').then((m) => m.EntrarClinicaPage),
        ),
        Lancamentos: page(() =>
          import('./pages/clinica/LancamentosPage.js').then((m) => m.LancamentosPage),
        ),
        Saude: page(() => import('./pages/saude/SaudePage.js').then((m) => m.SaudePage)),
        SaudeClinica: page(() =>
          import('./pages/saude/SaudeClinicaPage.js').then((m) => m.SaudeClinicaPage),
        ),
        EvolucaoAluno: page(() =>
          import('./pages/saude/EvolucaoAlunoPage.js').then((m) => m.EvolucaoAlunoPage),
        ),
        Evolucao: page(() => import('./pages/clinica/EvolucaoPage.js').then((m) => m.EvolucaoPage)),
        TreinoAluno: page(() =>
          import('./pages/saude/TreinoAlunoPage.js').then((m) => m.TreinoAlunoPage),
        ),
        Registros: page(() =>
          import('./pages/saude/RegistrosPage.js').then((m) => m.RegistrosPage),
        ),
        ClinicaPublica: page(() =>
          import('./pages/clinica/ClinicaPublicaPage.js').then((m) => m.ClinicaPublicaPage),
        ),
        Pagar: page(() =>
          import('./pages/clinica/PagarCobrancaPage.js').then((m) => m.PagarCobrancaPage),
        ),
        Confirmar: page(() =>
          import('./pages/clinica/ConfirmarConsultaPage.js').then((m) => m.ConfirmarConsultaPage),
        ),
      };

/** Página pública da clínica (`/c/:slug`), também para quem chega sem conta (`App.tsx`). */
export const ClinicaPublicaPage = PAGES?.ClinicaPublica ?? null;
export const FormularioPublicoPage = PAGES?.Formulario ?? null;
/** Confirmar a consulta pelo link do lembrete (`/c/:slug/confirmar/:token`), sem conta. */
export const ConfirmarConsultaPage = PAGES?.Confirmar ?? null;
/** Pagar pelo link da cobrança (`/c/:slug/pagar/:token`), sem conta. */
export const PagarCobrancaPage = PAGES?.Pagar ?? null;

/** As rotas, para dentro do `Switch` de quem está na conta (lista vazia no pacote da loja). */
export function clinicRoutes() {
  if (!PAGES) return [];
  return [
    <Route key="c-hoje" path="/negocio/clinica" component={PAGES.Hoje} />,
    <Route key="c-nova" path="/negocio/clinica/nova" component={PAGES.Nova} />,
    <Route key="c-agenda" path="/negocio/clinica/agenda" component={PAGES.Agenda} />,
    <Route key="c-pacientes" path="/negocio/clinica/pacientes" component={PAGES.Pacientes} />,
    <Route
      key="c-evolucao"
      path="/negocio/clinica/pacientes/:id/evolucao"
      component={PAGES.Evolucao}
    />,
    <Route key="c-corpo" path="/negocio/clinica/pacientes/:id/corpo" component={PAGES.Corpo} />,
    <Route key="c-paciente" path="/negocio/clinica/pacientes/:id" component={PAGES.Paciente} />,
    <Route
      key="c-atendimento"
      path="/negocio/clinica/atendimento/:id"
      component={PAGES.Atendimento}
    />,
    <Route key="c-leads" path="/negocio/clinica/leads" component={PAGES.Leads} />,
    <Route key="c-tarefas" path="/negocio/clinica/tarefas" component={PAGES.Tarefas} />,
    <Route key="c-financeiro" path="/negocio/clinica/financeiro" component={PAGES.Financeiro} />,
    <Route
      key="c-medicamentos"
      path="/negocio/clinica/medicamentos"
      component={PAGES.Medicamentos}
    />,
    <Route key="c-alimentos" path="/negocio/clinica/alimentos" component={PAGES.Alimentos} />,
    <Route key="c-plano" path="/negocio/clinica/plano/:id" component={PAGES.Plano} />,
    <Route key="c-treino" path="/negocio/clinica/treino/:id" component={PAGES.FichaTreino} />,
    <Route key="c-treinos" path="/negocio/clinica/treinos" component={PAGES.Treinos} />,
    <Route key="c-video" path="/negocio/clinica/videos/:id" component={PAGES.Video} />,
    <Route key="c-diarios" path="/negocio/clinica/diarios" component={PAGES.Diarios} />,
    <Route key="c-relatorios" path="/negocio/clinica/relatorios" component={PAGES.Relatorios} />,
    <Route key="c-numeros" path="/negocio/clinica/numeros" component={PAGES.Numeros} />,
    <Route key="c-mais" path="/negocio/clinica/mais" component={PAGES.Mais} />,
    <Route key="c-pagina" path="/negocio/clinica/pagina" component={PAGES.PaginaPublica} />,
    <Route key="c-catalogo" path="/negocio/clinica/catalogo" component={PAGES.Catalogo} />,
    <Route key="c-equipe" path="/negocio/clinica/equipe" component={PAGES.Equipe} />,
    <Route key="c-importar" path="/negocio/clinica/importar" component={PAGES.Importar} />,
    <Route key="c-importacao" path="/negocio/clinica/importar/:id" component={PAGES.Importacao} />,
    <Route key="c-entrar" path="/negocio/clinica/entrar" component={PAGES.Entrar} />,
    <Route key="c-lancamentos" path="/negocio/clinica/lancamentos" component={PAGES.Lancamentos} />,
    <Route key="s-saude" path="/saude" component={PAGES.Saude} />,
    <Route key="s-registros" path="/saude/registros" component={PAGES.Registros} />,
    <Route key="s-treino" path="/saude/clinica/:clinicId/treino" component={PAGES.TreinoAluno} />,
    <Route
      key="s-evolucao"
      path="/saude/clinica/:clinicId/evolucao"
      component={PAGES.EvolucaoAluno}
    />,
    <Route key="s-corpo" path="/saude/clinica/:clinicId/corpo" component={PAGES.CorpoAluno} />,
    <Route
      key="s-video"
      path="/saude/clinica/:clinicId/video/:videoId"
      component={PAGES.VideoAluno}
    />,
    <Route key="s-clinica" path="/saude/clinica/:clinicId" component={PAGES.SaudeClinica} />,
    <Route key="c-pagar" path="/c/:slug/pagar/:token" component={PAGES.Pagar} />,
    <Route key="c-confirmar" path="/c/:slug/confirmar/:token" component={PAGES.Confirmar} />,
    <Route key="c-publica" path="/c/:slug" component={PAGES.ClinicaPublica} />,
    <Route key="c-formulario" path="/f/:token" component={PAGES.Formulario} />,
  ];
}
